import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TutorialProvider, useTutorial } from './contexts/TutorialContext';
import TutorialOverlay from './components/TutorialOverlay';
import { Login } from './components/Login';
import { LanguageToggle } from './components/LanguageToggle';
import ProjectIndicator from './components/ProjectIndicator';
import ProjectArtifactsModal from './components/ProjectArtifactsModal';
import { HelpProvider } from './contexts/HelpContext';
import { useActiveProject } from './hooks/useActiveProject';
import { useTranslation } from 'react-i18next';
import i18n from './i18n/i18n';
import ProcessFlowHub from './pages/ProcessFlowHub';

const LazyPartReference = React.lazy(() => import('./pages/PartReference'));
const LazyGuidePage = React.lazy(() => import('./pages/GuidePage'));

/**
 * Custom event name used by Hub + ProjectIndicator to ask the
 * PlatformShell-mounted ProjectArtifactsModal to open. Centralised
 * here so any component can dispatch it without needing a context
 * provider above its mount point.
 */
export const OPEN_PROJECT_ARTIFACTS_EVENT = 'ugp:open-project-artifacts';

/** Convenience helper for callers that don't want to construct the
 *  CustomEvent themselves. */
export function openProjectArtifactsModal(): void {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_PROJECT_ARTIFACTS_EVENT));
  } catch { /* noop in non-DOM env */ }
}

const LazyPlannerApp = React.lazy(() => import('./App'));
const LazyDesignGrid = React.lazy(() => import('./pages/DesignGrid'));
const LazyDesignPlant = React.lazy(() => import('./pages/DesignPlant'));
const LazyPredict = React.lazy(() => import('./pages/Predict'));
const LazyExclusions = React.lazy(() => import('./pages/Exclusions'));
const LazyHydro = React.lazy(() => import('./pages/Hydro'));
const LazyNewSite = React.lazy(() => import('./pages/NewSite'));
const LazyLoadProfile8760 = React.lazy(() => import('./pages/LoadProfile8760Page'));
const LazyCdfSourcesAdmin = React.lazy(() => import('./pages/CdfSourcesAdminPage'));
const LazyMyTraining = React.lazy(() => import('./pages/MyTraining'));
const LazyTopologyCompare = React.lazy(() => import('./pages/TopologyCompare'));
const LazyGenerationLayout = React.lazy(() => import('./pages/GenerationLayout'));
const LazyTensionCalculator = React.lazy(() => import('./pages/TensionCalculator'));
const LazyGantt = React.lazy(() => import('./pages/GanttPage'));
const LazyPortfolioMap = React.lazy(() => import('./pages/PortfolioMapPage'));

/** Unified app nav (hub cards). 8760: standalone `/8760`; Design Plant uses TMY + sizing. */
const NAV_ITEMS = [
  { path: '/', label: 'Hub', icon: '🏠' },
  { path: '/design-grid', label: 'Design Grid', icon: '🔌' },
  { path: '/topology-compare', label: 'Topology Compare', icon: '⚖️' },
  { path: '/design-plant', label: 'Design Plant', icon: '☀️' },
  { path: '/8760', label: '8760 Load', icon: '📊' },
  { path: '/planner', label: 'Track Construction', icon: '🏗️' },
  { path: '/portfolio-map', label: 'Portfolio map', icon: '🗺️' },
  { path: '/tension', label: 'Tension Calc', icon: '📐' },
  { path: '/gantt', label: 'Gantt', icon: '🗓️' },
  { path: '/predict', label: 'Predict Progress', icon: '📈' },
  /** In-app user guide (same as 📖 next to logo). Kept in the scrollable nav so it is always listed with other tools. */
  { path: '/guide', label: 'Help', icon: '📖' },
];

function PlatformNav() {
  const { authUser, handleLogout } = useAuth();
  const tutorial = useTutorial();
  const location = useLocation();
  const { t } = useTranslation();

  if (location.pathname.startsWith('/planner')) return null;

  return (
    <div className="bg-gray-900 text-white">
      {/* logo / nav / auth+project. Narrow viewports: left uses min-w-0 +
          overflow-x nav; right uses min-w-0 shrink + flex-wrap and keeps
          ProjectIndicator last with a max-width so the chip truncates
          instead of covering Tour / language / logout. */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex items-center gap-4 min-w-0 flex-1 basis-[min(100%,28rem)] sm:basis-auto">
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <img
              src="https://1pwrafrica.com/wp-content/uploads/2018/11/logo.png"
              alt="1PWR Africa"
              className="h-8 object-contain"
            />
            <span className="text-lg font-bold tracking-tight group-hover:text-blue-300 transition-colors hidden sm:inline">
              uGrid Platform
            </span>
          </Link>
          <Link
            to="/guide"
            className="inline-flex items-center gap-1 text-gray-300 hover:text-white text-xs sm:text-sm font-medium px-2 py-1.5 shrink-0 border border-white/20 rounded-md hover:bg-white/10 transition-colors"
            title={t('help.title', 'User guide and documentation')}
          >
            <span aria-hidden>📖</span>
            <span className="hidden sm:inline">{t('header.help')}</span>
          </Link>
          <nav
            className={`items-center gap-1 min-w-0 flex-1 ${
              /* Wrap whenever items overflow. A nowrap + hidden-scrollbar row
                 clipped trailing tools (Gantt / Predict / Help) — during the
                 tour that hid the Predict Progress control the overlay asks
                 the user to click. */
              tutorial.active ? 'flex flex-wrap gap-y-1' : 'hidden md:flex md:flex-wrap gap-y-1'
            }`}
          >
            {NAV_ITEMS.map(item => {
              const isActive =
                item.path === '/'
                  ? location.pathname === '/'
                  : item.path === '/guide'
                    ? location.pathname === '/guide'
                    : location.pathname.startsWith(item.path);
              const tutorialNav =
                item.path === '/'
                  ? 'tutorial-top-nav-hub'
                  : item.path === '/design-grid'
                    ? 'tutorial-top-nav-design-grid'
                    : item.path === '/topology-compare'
                      ? 'tutorial-top-nav-topology-compare'
                      : item.path === '/design-plant'
                      ? 'tutorial-top-nav-design-plant'
                      : item.path === '/8760'
                        ? 'tutorial-top-nav-8760'
                        : item.path === '/planner'
                          ? 'tutorial-top-nav-track-construction'
                          : item.path === '/gantt'
                            ? 'tutorial-top-nav-gantt'
                            : item.path === '/predict'
                              ? 'tutorial-top-nav-predict'
                              : undefined;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-tutorial={tutorialNav}
                  data-tut-genlay={
                    item.path === '/design-plant'
                      ? 'design-plant'
                      : item.path === '/planner'
                        ? 'track-construction'
                        : undefined
                  }
                  className={`px-2.5 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                    isActive
                      ? 'bg-white/15 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.icon} {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-2 text-sm min-w-0 shrink flex-wrap justify-end">
          {!tutorial.active && (
            <button
              onClick={tutorial.start}
              className="text-gray-400 hover:text-white transition-colors px-2 py-1 whitespace-nowrap shrink-0"
              title={t('header.guidedTourTitle', 'Start the guided platform tutorial')}
            >
              {t('header.guidedTour', 'Guided Tour')}
            </button>
          )}
          <LanguageToggle />
          <Link
            to="/my-training"
            className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${
              location.pathname.startsWith('/my-training')
                ? 'bg-white/15 text-white'
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
          >
            {t('training.title', 'My Training')}
          </Link>
          <span className="text-gray-300 whitespace-nowrap hidden lg:inline shrink-0">
            {t('header.signedInAs')} <strong className="text-white">{authUser}</strong>
          </span>
          {/* Compact, always-visible identity badge for narrow widths */}
          <span
            className="text-gray-300 whitespace-nowrap lg:hidden shrink-0"
            title={`${t('header.signedInAs')} ${authUser}`}
          >
            <strong className="text-white">{authUser}</strong>
          </span>
          <button
            onClick={handleLogout}
            className="bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded transition-colors whitespace-nowrap shrink-0"
          >
            {t('header.logout')}
          </button>
          {/*
            Project chip last so long names truncate toward the viewport edge
            instead of overlaying Tour / language / auth (flex order was causing
            overlap when the row couldn't shrink).
          */}
          <ProjectIndicator className="shrink min-w-0 max-w-[min(13rem,calc(100vw-10rem))]" />
        </div>
      </div>
    </div>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { authUser, authError, handleLogin } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    if (lang === 'fr' || lang === 'en') {
      localStorage.setItem('ugridplan-language', lang);
      i18n.changeLanguage(lang);
    }
  }, []);

  if (authUser === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white border-2 border-blue-300 rounded-lg p-8 max-w-md text-center shadow-lg">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            <div className="text-lg font-semibold text-gray-800">Checking authentication…</div>
          </div>
          <p className="text-sm text-gray-600">
            Connecting to backend server.
          </p>
        </div>
      </div>
    );
  }

  if (authUser === null) {
    // Centralized auth: sign in at Nexus, which SSOs back via the adapter's
    // GET /api/auth/sso (sets the access_token cookie). ?fallback=1 keeps the
    // local login reachable for emergencies (e.g. Nexus outage) — the SSO
    // receiver also lands here with ?fallback=1&sso_error= on failure, so a
    // broken exchange never redirect-loops.
    const params = new URLSearchParams(window.location.search);
    if (params.get('fallback') !== '1') {
      const returnTo = window.location.pathname + window.location.search;
      window.location.replace(
        'https://nexus.1pwrafrica.com/sso/authorize?tool=ugp&redirect_uri=' +
          encodeURIComponent(
            'https://ugp.1pwrafrica.com/api/auth/sso?return=' + encodeURIComponent(returnTo)
          )
      );
      return null;
    }
    return <Login onLogin={handleLogin} error={authError} />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ProcessFlowHub />} />
      <Route path="/parts/:partId" element={<React.Suspense fallback={<LoadingFallback label="Loading part reference…" />}><LazyPartReference /></React.Suspense>} />
      <Route
        path="/guide"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading guide…" />}>
            <LazyGuidePage />
          </React.Suspense>
        }
      />
      <Route
        path="/planner/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading uGridPLAN..." />}>
            <HelpProvider>
              <LazyPlannerApp />
            </HelpProvider>
          </React.Suspense>
        }
      />
      <Route
        path="/design-grid/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Design Grid..." />}>
            <LazyDesignGrid />
          </React.Suspense>
        }
      />
      <Route
        path="/topology-compare/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Topology Compare..." />}>
            <LazyTopologyCompare />
          </React.Suspense>
        }
      />
      <Route
        path="/design-plant/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Design Plant..." />}>
            <LazyDesignPlant />
          </React.Suspense>
        }
      />
      <Route
        path="/8760/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading 8760 Load Profile..." />}>
            <LazyLoadProfile8760 />
          </React.Suspense>
        }
      />
      <Route
        path="/my-training"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading training…" />}>
            <LazyMyTraining />
          </React.Suspense>
        }
      />
      <Route
        path="/admin/cdf-sources"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading CDF registry…" />}>
            <LazyCdfSourcesAdmin />
          </React.Suspense>
        }
      />
      <Route
        path="/predict/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Predictions..." />}>
            <LazyPredict />
          </React.Suspense>
        }
      />
      <Route
        path="/exclusions/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Exclusion Zones..." />}>
            <LazyExclusions />
          </React.Suspense>
        }
      />
      <Route
        path="/hydro/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Hydro context..." />}>
            <LazyHydro />
          </React.Suspense>
        }
      />
      <Route
        path="/new-site/*"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading New Site..." />}>
            <LazyNewSite />
          </React.Suspense>
        }
      />
      <Route
        path="/generation-layout"
        element={
          <div className="h-full min-h-0">
            <React.Suspense fallback={<LoadingFallback label="Loading Layout Editor..." />}>
              <LazyGenerationLayout />
            </React.Suspense>
          </div>
        }
      />
      <Route
        path="/tension"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Tension Calculator..." />}>
            <LazyTensionCalculator />
          </React.Suspense>
        }
      />
      <Route
        path="/gantt"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Gantt..." />}>
            <LazyGantt />
          </React.Suspense>
        }
      />
      <Route
        path="/portfolio-map"
        element={
          <React.Suspense fallback={<LoadingFallback label="Loading Portfolio map..." />}>
            <LazyPortfolioMap />
          </React.Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Main content scrolls inside #platform-main-scroll; reset on navigation so long pages (e.g. Predict) start at the top. */
function ScrollMainToTopOnRoute() {
  const { pathname } = useLocation();
  useEffect(() => {
    const el = document.getElementById('platform-main-scroll');
    if (el) el.scrollTop = 0;
  }, [pathname]);
  return null;
}

function LoadingFallback({ label }: { label: string }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
        <p className="text-gray-600 font-medium">{label}</p>
      </div>
    </div>
  );
}

/**
 * Shell-mounted Project Artifacts modal. Single instance for the
 * whole app; opened by dispatching ``OPEN_PROJECT_ARTIFACTS_EVENT``
 * from anywhere (Hub, ProjectIndicator dropdown). Reads the active
 * project name from the same hook the indicator uses so the modal
 * always targets the right project without prop-drilling.
 */
function GlobalProjectArtifactsModal() {
  const [open, setOpen] = useState(false);
  const { projectName } = useActiveProject();
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_PROJECT_ARTIFACTS_EVENT, handler);
    return () => window.removeEventListener(OPEN_PROJECT_ARTIFACTS_EVENT, handler);
  }, []);
  return (
    <ProjectArtifactsModal
      isOpen={open}
      onClose={() => setOpen(false)}
      projectName={projectName || null}
    />
  );
}

export default function PlatformShell() {
  return (
    <BrowserRouter>
      <ScrollMainToTopOnRoute />
      <AuthProvider>
        <AuthGate>
          <TutorialProvider>
            <div className="h-screen flex flex-col">
              <PlatformNav />
              <div id="platform-main-scroll" className="flex-1 min-h-0 overflow-auto">
                <AppRoutes />
              </div>
            </div>
            <TutorialOverlay />
            <GlobalProjectArtifactsModal />
          </TutorialProvider>
        </AuthGate>
      </AuthProvider>
    </BrowserRouter>
  );
}
